// Android-only Settings controls (docs/06 §4): "stay reachable" foreground
// service and the push-transport picker (FCM default, ntfy/UnifiedPush for
// Google-blocked environments). Bridges the Settings screen to the shell's
// Capacitor seams while honoring the shell's composition rule: probe
// `isCapacitorNative()` first (plain global check, web-safe), and only then
// dynamically import `shell/capacitor/index.js` — the only module here that
// pulls @capacitor/core. `integrations.js` is web-safe (type-only plugin
// imports) so the seam functions themselves are imported statically.

import type { UpdateSessionRequest } from '@merkury/protocol';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import {
  PUSH_TRANSPORT_KEY,
  STAY_REACHABLE_KEY,
  setStayReachable,
  syncCapacitorPush,
  type PushTransport,
} from '../../shell/capacitor/integrations.js';
import type { MerkuryPlugins } from '../../shell/capacitor/plugins.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';

export type { PushTransport };

/** The native handles the controls need, resolved once behind the probe. */
export interface NativeSettingsHandles {
  kv: KvStore;
  callService: MerkuryPlugins['callService'];
  push: MerkuryPlugins['push'];
}

export interface NativeSettingsState {
  stayReachable: boolean;
  pushTransport: PushTransport;
  /** Only meaningful after picking ntfy: is a UnifiedPush distributor there? */
  ntfyAvailable: boolean | null;
  /** Doze exemption granted? null = unknown (plugin missing/failed). */
  batteryExempt: boolean | null;
}

export interface NativeSettingsBridge {
  isNative(): boolean;
  load(): Promise<NativeSettingsState>;
  /**
   * Flips the mode; when enabling without a doze exemption it also fires the
   * system exemption dialog once (part of the toggle flow, not a nag). The
   * returned flag is the pre-dialog state — re-check with refreshBatteryExempt
   * when the app regains visibility.
   */
  setStayReachable(enabled: boolean): Promise<{ batteryExempt: boolean | null }>;
  /** Re-read the doze exemption (e.g. after returning from the system dialog). */
  refreshBatteryExempt(): Promise<boolean | null>;
  /** Re-fire the system exemption dialog (the "allow" link in the note). */
  requestBatteryExemption(): Promise<void>;
  /**
   * Persist the transport and re-run the shell's push sync so it takes effect
   * immediately (falls back to FCM when no distributor is installed — the
   * returned flag drives the UI hint).
   */
  setPushTransport(
    transport: PushTransport,
    updateSession: (body: UpdateSessionRequest) => Promise<void>,
  ): Promise<{ ntfyAvailable: boolean | null }>;
}

async function defaultLoadHandles(): Promise<NativeSettingsHandles> {
  const native = await import('../../shell/capacitor/index.js');
  const plugins = native.nativePlugins();
  return { kv: native.createNativeKv(), callService: plugins.callService, push: plugins.push };
}

export function createNativeSettings(
  opts: {
    isNative?: () => boolean;
    loadHandles?: () => Promise<NativeSettingsHandles>;
  } = {},
): NativeSettingsBridge {
  const probe = opts.isNative ?? isCapacitorNative;
  const loader = opts.loadHandles ?? defaultLoadHandles;
  let handles: Promise<NativeSettingsHandles> | undefined;
  const getHandles = (): Promise<NativeSettingsHandles> => (handles ??= loader());

  const batteryExemptOf = async (
    callService: MerkuryPlugins['callService'],
  ): Promise<boolean | null> => {
    try {
      return (await callService.getBatteryOptimizationStatus()).exempt;
    } catch {
      return null; // older shell / plugin missing: state simply unknown
    }
  };

  return {
    isNative: () => probe(),

    async load(): Promise<NativeSettingsState> {
      const { kv, callService } = await getHandles();
      const [stayReachable, pushTransport, batteryExempt] = await Promise.all([
        kv.get<boolean>(STAY_REACHABLE_KEY).catch(() => undefined),
        kv.get<PushTransport>(PUSH_TRANSPORT_KEY).catch(() => undefined),
        batteryExemptOf(callService),
      ]);
      return {
        // Stay-reachable defaults ON: absence of a stored choice now means
        // enabled (settings-side read only — the shell/native boot default is
        // flipped separately). Connection > everything: reachability is the
        // safe default under censorship.
        stayReachable: stayReachable ?? true,
        pushTransport: pushTransport ?? 'fcm',
        ntfyAvailable: null,
        batteryExempt,
      };
    },

    async setStayReachable(enabled: boolean): Promise<{ batteryExempt: boolean | null }> {
      const { kv, callService } = await getHandles();
      // The shell's seam: persists the choice AND flips the foreground service.
      await setStayReachable(enabled, kv, callService);
      if (!enabled) return { batteryExempt: null };
      const batteryExempt = await batteryExemptOf(callService);
      if (batteryExempt === false) {
        // Ask exactly when the user opts into reachability. Declining keeps
        // the weaker mode; the Settings row notes it.
        await callService.requestBatteryOptimizationExemption().catch(() => undefined);
      }
      return { batteryExempt };
    },

    async refreshBatteryExempt(): Promise<boolean | null> {
      const { callService } = await getHandles();
      return batteryExemptOf(callService);
    },

    async requestBatteryExemption(): Promise<void> {
      const { callService } = await getHandles();
      await callService.requestBatteryOptimizationExemption().catch(() => undefined);
    },

    async setPushTransport(
      transport: PushTransport,
      updateSession: (body: UpdateSessionRequest) => Promise<void>,
    ): Promise<{ ntfyAvailable: boolean | null }> {
      const { kv, push } = await getHandles();
      await kv.put(PUSH_TRANSPORT_KEY, transport);
      let ntfyAvailable: boolean | null = null;
      if (transport === 'ntfy') {
        ntfyAvailable = (await push.isNtfyDistributorAvailable().catch(() => ({ available: false })))
          .available;
      }
      // Re-run the shell's sync so the new choice applies now, not next boot.
      await syncCapacitorPush({ push, kv, updateSession }).catch(() => undefined);
      return { ntfyAvailable };
    },
  };
}
